import { ReactElement, useEffect, useId, useRef, useState, useCallback } from "react";
import { Flex } from "@jsq/ds/core";
import { TreeWrapper, TreeContainer } from "./style_vanilla.css";
import { TreeEdge } from "./TreeEdge";
import { TreeNode } from "./TreeNode";
import { buildEdgeConfigs, validatePosition } from "./util";
import type { TreeConfig, EdgeStyle, ElementRegistry, TreeNodeData } from "./types";

export type TreeComponentProps = {
  data: TreeNodeData;
  treeConfig?: TreeConfig;
  edgeStyle?: EdgeStyle;
  rootEdgeStyle?: EdgeStyle;
};

export const TreeComponent = (props: TreeComponentProps): ReactElement | null => {
  const instanceId = useId().replace(/:/g, "");
  const scrollContainer = useRef<HTMLDivElement>(null);
  const treeWrapper = useRef<HTMLDivElement>(null);
  const elementRegistry = useRef<ElementRegistry>(new Map());
  const [containerBounds, setContainerBounds] = useState<DOMRect>();

  const updateContainerBounds = useCallback(() => {
    if (treeWrapper.current) {
      const newBounds = treeWrapper.current.getBoundingClientRect();
      setContainerBounds((prev) =>
        !prev ||
        Math.abs(prev.width - newBounds.width) > 1 ||
        Math.abs(prev.height - newBounds.height) > 1 ||
        Math.abs(prev.left - newBounds.left) > 1 ||
        Math.abs(prev.top - newBounds.top) > 1
          ? newBounds
          : prev,
      );
    }
  }, []);

  const handleScroll = useCallback(() => {
    updateContainerBounds();
  }, [updateContainerBounds]);

  useEffect(() => {
    const wrapperEl = treeWrapper.current;
    const scrollEl = scrollContainer.current;
    const observer = new ResizeObserver(() => {
      updateContainerBounds();
    });

    if (wrapperEl) {
      observer.observe(wrapperEl);
      updateContainerBounds();
    }

    if (scrollEl) {
      scrollEl.addEventListener("scroll", handleScroll);
    }

    return () => {
      observer.disconnect();
      if (scrollEl) {
        scrollEl.removeEventListener("scroll", handleScroll);
      }
    };
  }, [updateContainerBounds, handleScroll]);

  if (!props.data) {
    return <div role="alert">Failed to render tree as `data` is missing</div>;
  }

  const treeConfig = validatePosition(props.treeConfig);

  return (
    <Flex
      ref={scrollContainer}
      flexDirection="column"
      className={TreeContainer}
      data-testid="tree-component"
    >
      <div ref={treeWrapper} className={TreeWrapper}>
        <TreeNode
          treeNodes={[props.data]}
          level={0}
          treeConfig={treeConfig}
          instanceId={instanceId}
          elementRegistry={elementRegistry.current}
        />
      </div>
      <TreeEdge
        edgeConfigs={buildEdgeConfigs(
          props.data,
          instanceId,
          props.edgeStyle,
          props.rootEdgeStyle,
        )}
        treeConfig={treeConfig}
        containerBounds={containerBounds}
        instanceId={instanceId}
        elementRegistry={elementRegistry.current}
      />
    </Flex>
  );
};
